import {
  Component,
  computed,
  HostListener,
  inject,
  OnInit,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import packageJSON from 'src/../package.json';
import {
  ClientsService,
  SelectedClient
} from 'src/app/services/clients/clients.service';
import { UsersService } from 'src/app/services/users/users.service';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { environment } from 'src/environments/environment';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { PayeeCasesService } from 'src/app/shared/payee-cases/payee-cases.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  imports: [
    RouterLink,
    PayeeButtonComponent,
    MatMenuTrigger,
    MatTooltip,
    MatMenu,
    MatMenuItem,
    PayeeIconComponent,
    RouterLinkActive,
    MatButton,
    TranslateModule,
  ],
  host: {
    '[class.close]': 'close()',
  },
})
export class SidebarComponent implements OnInit {
  private readonly clientsService = inject(ClientsService);
  private readonly usersService = inject(UsersService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly payeeCasesService = inject(PayeeCasesService);

  private readonly user = toSignal(this.usersService.userSubject);
  readonly isMobile = signal(false);
  readonly open = signal(true);
  readonly clientOptions = toSignal(this.clientsService.clientsSubject);
  readonly hasMultipleClients = computed(() => {
    const clients = this.clientOptions();
    if (!clients) {
      return false;
    }
    return clients.length > 1;
  });
  readonly selectedClient = toSignal(this.clientsService.selectedClientSubject);
  readonly clientFilter = toSignal(this.payeeCasesService.client);
  readonly clientFilterQueryParams = computed((): Record<string, string> => {
    const uuid = this.clientFilter()?.uuid;
    if (!uuid) {
      return {};
    }
    return { client_id: uuid };
  });
  readonly displayedClientName = computed((): string | null => {
    const filterName = this.clientFilter()?.name;
    if (filterName) {
      return filterName;
    }

    if (!this.hasMultipleClients()) {
      return this.selectedClient()?.name ?? null;
    }

    return null;
  });
  readonly hasSzamlazzhuPartner = computed((): boolean => {
    const clients = this.clientOptions();
    if (!clients) {
      return false;
    }

    return clients.some(c => c.partner_integrations.includes('szamlazzhu'));
  });
  readonly userName = computed((): string => this.user()?.name ?? '');
  readonly payeeEmail = computed((): string => {
    const client = this.selectedClient();
    if (client?.partner_integrations.includes('ehaz')) {
      return 'hello+ehaz@payee.tech';
    } else {
      return 'hello@payee.tech';
    }
  });

  readonly close = computed(() => !this.open() || this.isMobile());
  @HostListener('window:resize') setIsMobile(): void {
    this.isMobile.set(window.innerWidth <= 768 || screen.width <= 768);
  }

  get versionNumber() {
    return packageJSON.version;
  }

  get isProduction(): boolean {
    return environment.production;
  }

  ngOnInit(): void {
    this.setIsMobile();
  }

  toggleOpen() {
    this.open.update((value) => !value);
  }

  navigateToClientProfile(): void {
    const client = this.selectedClient();
    if (client) {
      this.clientsService.selectClientWithCaseFilter(client);
    }
    this.router.navigate(['/user/client-profile']);
  }

  navigateToUserProfile(): void {
    this.router.navigate(['/user/user-profile']);
  }

  selectClient(client: SelectedClient | null): void {
    if (client) {
      this.clientsService.selectClientWithCaseFilter(client);
    } else {
      this.payeeCasesService.client.next(null);
    }
  }

  async logout(): Promise<void> {
    await this.authService.logout();
  }
}
